import { useEffect } from 'react';
import useSelectedCommunity from './useSelectedCommunity';
import { SubscriptionType } from '../../types/misc';
import { isSubscriptionGreaterThanOrEqual } from '../../utils/subscriptionUtils';
import { featuresInSubscription, getMinimumSubscriptionForFeatures } from '../../utils/FeatureUtils';
import communityService from '../../services/community/communityService';

export default function useActiveSubscription(): SubscriptionType {
  const community = useSelectedCommunity();

  // Derive active subscription type (safe to compute during render)
  let subscriptionType: SubscriptionType = SubscriptionType.FREE;
  if (community) {
    if (community.trialEndDate && new Date(community.trialEndDate) > new Date()) {
      subscriptionType = community.trialType || SubscriptionType.FREE;
    } else {
      subscriptionType = community.subscription?.type ?? SubscriptionType.FREE;
    }
  }

  // Feature-downgrade side-effect must run AFTER render, not during it
  useEffect(() => {
    if (!community) return;
    if (!community.trialEndDate && !community.subscription) return;

    const requiredSubscription = getMinimumSubscriptionForFeatures(community.features || {});
    if (!isSubscriptionGreaterThanOrEqual(subscriptionType, requiredSubscription)) {
      const { name, description, features, memberPermissions } = community;
      if (!features) return;
      const allowedFeatures = featuresInSubscription[subscriptionType];
      const featuresCopy = { ...features };
      Object.keys(featuresCopy).forEach((key) => {
        if (!allowedFeatures[key as keyof typeof allowedFeatures]) {
          featuresCopy[key as keyof typeof featuresCopy] = false;
        }
      });
      communityService.updateCommunity(
        name,
        description,
        (memberPermissions || {}) as import('../../types/community').CommunityMemberPermissions,
        featuresCopy,
      );
    }
  }, [community?.id, subscriptionType]);

  return subscriptionType;
}
